<template>
	<div
		class="pronto-toggle"
		:class="{ active: value, disabled, loading, 'force-light': light }"
		@click="$emit('click', $event)"
	>
		<input
			type="checkbox"
			class="pronto-toggle-checkbox"
			v-model="value"
			:disabled="disabled"
		/>
		<div class="pronto-toggle-button">
			<Spinner v-if="loading" />
		</div>
		<div
			class="pronto-toggle-disabled"
			v-if="disabled"
			@click="$emit('click', $event)"
		/>
	</div>
</template>

<script>
import { setColorSchemeForElement } from '@/helpers/colors'
import Spinner from './Spinner'
import { colorSchemes } from '@/helpers/constants'

export default {
	name: 'ToggleSwitch',
	components: { Spinner },
	props: {
		modelValue: { type: Boolean, default: null },
		disabled: { type: Boolean, default: null },
		loading: { type: Boolean, default: null },
		light: { type: Boolean, default: null },
		dark: { type: Boolean, default: null },
	},

	data() {
		return { value: this.modelValue, focused: false }
	},
	emits: ['update:modelValue', 'change', 'click'],
	watch: {
		modelValue(modelValue) {
			this.value = modelValue
		},
		value(value) {
			this.$emit('update:modelValue', value)
			this.$emit('change', value) // additional event to handle v2/v3 backwards compatibility
		},
	},
	mounted() {
		if (this.light) {
			setColorSchemeForElement(this.$el, colorSchemes.LIGHT)
		} else if (this.dark) {
			setColorSchemeForElement(this.$el, colorSchemes.DARK)
		}
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$btn-size: calc(1em - 4px);

.pronto-toggle {
	cursor: pointer;
	position: relative;
	display: inline-block;
	height: 1em;
	width: 2em;
	border: 1px solid var(--neutral-200);
	background: var(--neutral-0);
	border-radius: 0.5em;
	transition: background 150ms ease-in-out;
	&:hover:not(.loading):not(.disabled) {
		.pronto-toggle-button {
			background: var(--neutral-200);
		}
	}
	&.active {
		background: var(--blue-300);
		.pronto-toggle-button {
			transform: translateX(1em);
		}
		&:hover:not(.loading):not(.disabled) {
			background: var(--blue-400);
		}
	}
	&.disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
	&.loading {
		opacity: 0.6;
		cursor: wait;
		.pronto-spinner {
			cursor: wait;
		}
	}
	.pronto-toggle-checkbox {
		cursor: pointer;
		opacity: 0;
		@include absolute-cover(1);
		width: 100%;
		height: 100%;
	}
	.pronto-toggle-button {
		width: $btn-size;
		height: $btn-size;
		border-radius: 0.5em;
		background: var(--neutral-100);
		border: 1px solid var(--neutral-200);
		transform: translateX(2px);
		transition: transform 150ms ease-in-out;
		margin-top: 1px;
		@include flex-center;
		.pronto-spinner {
			font-size: 20px;
			margin: -1px 0 0 1px;
		}
	}
	.pronto-toggle-disabled {
		opacity: 0;
		cursor: not-allowed;
		@include absolute-cover(2);
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.pronto-toggle {
		&:not(.force-light) {
			background: var(--neutral-500);
			border-color: var(--neutral-300);
			&:hover {
				background: var(--neutral-400);
				.pronto-toggle-button {
					background: var(--neutral-100);
				}
			}
			&.active {
				background: var(--teal-300);

				&:hover:not(.loading):not(.disabled) {
					background: var(--teal-300);
				}
			}
			.pronto-toggle-button {
				border-color: var(--neutral-300);
				background: var(--neutral-300);
			}
		}
	}
}
</style>
